import {observer} from "mobx-react";
import EditorPageLayout from "../../../../../../components/layouts/editor-page/editor-page-layout";
import useTranslation from "../../../../../../translation/hooks/translation-hook";
import {useCallback, useEffect, useState} from "react";
import {ConfirmModal} from "../../../../../../components/modal/components/confirm-modal/confirm-modal";
import useNavigationBlocker from "../../../../../../hooks/navigation-blocker-hook";
import ListLayout from "../../../../../../components/layouts/list-layout/list-layout";
import TranslationInput from "../../../../../../translation/translation-input/translation-input";
import packingSlip from "../../../../../../images/previews/packing-slip.svg";
import {PACKING_SLIP_TRANSLATION_KEYS} from "./constants";
import {FormFieldLine} from "../../../../../../components/form-field-line/form-field-line";
import currentPortalStore from "../../../../../../stores/current-portal-store";
import { Toggle } from "../../../../../../components/inputs/toggle/toggle";
import dashboardSettingsStore from "../../../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../../../models/ProfileCapabilities";

const PackingSlipTranslationsEditor: React.FC = observer(() => {
    useEffect(() => {
            currentPortalStore.load();
    },[]);

    const [isShowUnsaved, setIsShowUnsaved] = useState(false);

    const {
        getHint,
        hasChanges: hasTranslationChanges,
        discardChanges: translationsDiscardChanges,
        save: saveTranslations,
        isLoading,
    } = useTranslation();

    const packingSlipSettings = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_PACKING_SLIP_SETTINGS);

    const hasChanges = () => {
        return hasTranslationChanges() || (currentPortalStore.setting?.hasChanges() ?? false);
    }

    const {retryNavigation, unblockNavigation} = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const discardChanges = () => {
        translationsDiscardChanges();
    }

    const  isPageLoading = () => {
        return isLoading || currentPortalStore.setting?.isSaving() || currentPortalStore.isLoading;
    }

    const onSaveClicked = () => {
        const promised = [];

        if (hasTranslationChanges()) {
            promised.push(
                saveTranslations()
            );
        }

        if (currentPortalStore.setting?.hasChanges()) {
            promised.push(
                currentPortalStore.setting.save()
            );
        }

        Promise.all(promised).then(() => {
            unblockNavigation();
        })
    }
    return <>
        <EditorPageLayout
            toolbar={{
                saveButton: {
                    hasChanges: hasChanges(),
                    disabled: !hasChanges(),
                    onClick: onSaveClicked
                },
                isLoading: isPageLoading(),
            }}
        >
            <ListLayout>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 60
                }}>

                    <div className="preview">
                        <img src={packingSlip} alt=""/>
                    </div>

                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 20
                    }}>
                        {packingSlipSettings?.isPackingSlipForRmaNumberSupported && (
                            <FormFieldLine
                                label="Packing Slip for RMA #"
                                note={getHint(PACKING_SLIP_TRANSLATION_KEYS.PACKING_SLIP_FOR_RMA)}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.PACKING_SLIP_FOR_RMA}/>
                            </FormFieldLine>
                        )}

                        {packingSlipSettings?.isOrderIdSupported && (
                            <FormFieldLine
                                label="Order ID"
                                note={getHint(PACKING_SLIP_TRANSLATION_KEYS.ORDER_ID)}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.ORDER_ID}/>
                            </FormFieldLine>
                        )}

                        {packingSlipSettings?.isOrderIdBarcodeSupported && (
                            <FormFieldLine
                                label="Order ID Barcode"
                                note={getHint(PACKING_SLIP_TRANSLATION_KEYS.ORDER_ID_BARCODE)}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.ORDER_ID_BARCODE}/>
                            </FormFieldLine>
                        )}

                        {packingSlipSettings?.isWarehouseRmaIdSupported && (
                            <FormFieldLine
                                label="Warehouse RMA ID"
                                note={<>
                                    The RMA ID number for 3PL warehouses to use.&nbsp;
                                    <a rel="noreferrer" target="_blank" href="https://returngo.ai/go/kb/contact-support">Contact support</a> if you prefer to use it instead of the order ID barcode.
                                </>}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.WAREHOUSE_RMA_ID}/>
                            </FormFieldLine>
                        )}

                        {packingSlipSettings?.isWarehouseRmaBarcodeSupported && (
                            <FormFieldLine
                                label="Warehouse RMA Barcode"
                                note={getHint(PACKING_SLIP_TRANSLATION_KEYS.WAREHOUSE_RMA_ID_BARCODE)}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.WAREHOUSE_RMA_ID_BARCODE}/>
                            </FormFieldLine>
                        )}
                        
                        {packingSlipSettings?.isDisplayShipToAddressSupported && (
                            <FormFieldLine
                                label="Display Ship To Address"
                                note="Display the shipping address on the packing slip."
                            >
                                <Toggle
                                    label=""
                                    checked={currentPortalStore.setting?.showShipToAddress}
                                    onChange={(v) => currentPortalStore.setting?.setShowShipToAddress(v)}
                                />
                            </FormFieldLine>
                        )}

                        {packingSlipSettings?.isShipToSupported && (
                            <FormFieldLine
                                label="Ship to"
                                note={getHint(PACKING_SLIP_TRANSLATION_KEYS.SHIP_TO)}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.SHIP_TO}/>
                            </FormFieldLine>
                        )}

                        {packingSlipSettings?.isSentBySupported && (
                            <FormFieldLine
                                label="Sent by"
                                note={getHint(PACKING_SLIP_TRANSLATION_KEYS.SENT_BY)}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.SENT_BY}/>
                            </FormFieldLine>
                        )}

                        {packingSlipSettings?.isProductImageSupported && (
                            <FormFieldLine
                                label="Product Image"
                                note={getHint(PACKING_SLIP_TRANSLATION_KEYS.IMAGE)}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.IMAGE}/>
                            </FormFieldLine>
                        )}

                        {packingSlipSettings?.isProductNameSupported && (
                            <FormFieldLine
                                label="Product Name"
                                note={getHint(PACKING_SLIP_TRANSLATION_KEYS.PRODUCT_NAME)}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.PRODUCT_NAME}/>
                            </FormFieldLine>
                        )}

                        {packingSlipSettings?.isVariantSupported && (
                            <FormFieldLine
                                label="Variant"
                                note={getHint(PACKING_SLIP_TRANSLATION_KEYS.VARIANT)}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.VARIANT}/>
                            </FormFieldLine>
                        )}

                        {packingSlipSettings?.isSkuSupported && (
                            <FormFieldLine
                                label="SKU"
                                note={getHint(PACKING_SLIP_TRANSLATION_KEYS.SKU)}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.SKU}/>
                            </FormFieldLine>
                        )}

                        {packingSlipSettings?.isPriceSupported && (
                            <FormFieldLine
                                label="Price"
                                note={getHint(PACKING_SLIP_TRANSLATION_KEYS.PRICE)}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.PRICE}/>
                            </FormFieldLine>
                        )}

                        {packingSlipSettings?.isReturnReasonSupported && (
                            <FormFieldLine
                                label="Return Reason"
                                note={getHint(PACKING_SLIP_TRANSLATION_KEYS.RETURN_REASON)}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.RETURN_REASON}/>
                            </FormFieldLine>
                        )}

                        {packingSlipSettings?.isPackingSlipInstructionsSupported && (
                            <FormFieldLine
                                label="Packing Slip Instructions"
                                note={getHint(PACKING_SLIP_TRANSLATION_KEYS.PACKING_SLIP_INSTRUCTIONS)}
                            >
                                <TranslationInput translationKey={PACKING_SLIP_TRANSLATION_KEYS.PACKING_SLIP_INSTRUCTIONS}/>
                            </FormFieldLine>
                        )}
                    </div>
                </div>
            </ListLayout>
        </EditorPageLayout>
        {isShowUnsaved &&
            <ConfirmModal
                cancelButton={{
                    title: 'Cancel',
                    onClick: () => setIsShowUnsaved(false)
                }}
                okButton={{
                    title: 'Discard',
                    onClick: () => {
                        discardChanges();
                        setIsShowUnsaved(false);
                        retryNavigation();
                    }
                }}
            />
        }
    </>
});

export default PackingSlipTranslationsEditor;